import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useSobriety } from '@/contexts/SobrietyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { differenceInDays, parse, isValid, format } from 'date-fns';

export default function SobrietyInputScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const insets = useSafeAreaInsets();
  const [dateInput, setDateInput] = useState(format(new Date(), language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'));
  const [isLoading, setIsLoading] = useState(false);
  const { setupSobrietyExisting } = useSobriety();

  const handleStartCounting = async () => {
    // Parse the date input based on language
    const dateFormat = language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    const parsedDate = parse(dateInput, dateFormat, new Date());
    
    if (!isValid(parsedDate)) {
      console.error('Invalid date format');
      return;
    }

    // Calculate days between parsed date and today
    const today = new Date();
    const days = differenceInDays(today, parsedDate);
    
    // Validate: days should be >= 0 (not in future)
    if (days < 0) {
      console.error('Date cannot be in the future');
      return;
    }
    
    console.log('Starting sobriety count with', days, 'days (last drink:', parsedDate.toLocaleDateString(), ')');

    setIsLoading(true);
    try {
      // Call the context setup function with callback and wait for completion
      // DISABLED: Automatic Bill messages disabled per user request
      await setupSobrietyExisting(days, async (setupType, days) => {
        console.log('Sobriety setup completed:', setupType, days);
        // DISABLED: Daily encouragement message
        // await getDailyEncouragement();
      });

      console.log('Sobriety setup process finished, navigating...');

      // PRD §5.5 — `sobriety_input_submitted`. `days_set` is the
      // calculated distance between the user's last-drink date and today.
      // Fired only on successful setup (the catch branch below skips it).
      import('@/utils/clarityEvents')
        .then(({ logSobrietyInputSubmitted }) =>
          logSobrietyInputSubmitted({ days_set: days })
        )
        .catch(() => {});

      // Navigate to diary page to show sobriety clock
      router.replace('/diary');
    } catch (error) {
      console.error('Failed to setup sobriety:', error);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    router.push('/');
  };

  const formatDateInput = (text: string) => {
    // Remove non-numeric characters
    const numbers = text.replace(/\D/g, '');
    
    // Format based on language
    // English uses MM/DD/YYYY, Portuguese and Spanish use DD/MM/YYYY
    // The format is the same for both, just the semantic meaning changes
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  const handleDateChange = (text: string) => {
    const formatted = formatDateInput(text);
    setDateInput(formatted);
  };

  const calculateDaysFromInput = () => {
    const dateFormat = language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    const parsedDate = parse(dateInput, dateFormat, new Date());
    
    if (!isValid(parsedDate)) {
      return 0;
    }
    
    const days = differenceInDays(new Date(), parsedDate);
    return days >= 0 ? days : 0;
  };

  const formatDaysWithDots = (days: number) => {
    const localeMap: Record<string, string> = {
      pt: 'pt-BR',
      en: 'en-US',
      es: 'es-ES'
    };
    const locale = localeMap[language] || 'pt-BR';
    return days.toLocaleString(locale);
  };

  const isValidDate = () => {
    if (dateInput.length !== 10) return false;
    
    const dateFormat = language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    const parsedDate = parse(dateInput, dateFormat, new Date());
    
    if (!isValid(parsedDate)) return false;
    
    const days = differenceInDays(new Date(), parsedDate);
    return days >= 0; // Not in future
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <PageHeader 
          title={t('sobriety.input.title')} 
          onBack={handleCancel}
        />

        {/* Content */}
        <View style={styles.content}>
          <Text style={styles.subtitle}>
            {t('sobriety.input.subtitle')}
          </Text>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.textInput}
              value={dateInput}
              onChangeText={handleDateChange}
              placeholder={t('sobriety.input.placeholder')}
              placeholderTextColor="#A19F97"
              keyboardType="numeric"
              maxLength={10}
            />
            <Text style={styles.helperText}>
              {t('sobriety.input.helperText')}
            </Text>
          </View>

          {isValidDate() && (
            <View style={styles.resultContainer}>
              <Text style={styles.daysNumber}>
                {formatDaysWithDots(calculateDaysFromInput())}
              </Text>
              <Text style={styles.daysLabel}>
                {t('sobriety.input.daysLabel')}
              </Text>
            </View>
          )}
        </View>

        {/* Button */}
        <View style={[styles.buttonContainer, { paddingBottom: insets.bottom + 24 }]}>
          <PrimaryButton
            title={t('sobriety.input.button')}
            onPress={handleStartCounting}
            disabled={!isValidDate()}
            loading={isLoading}
          />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 24,
  },
  subtitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 29,
  },
  inputContainer: {
    width: '100%',
    gap: 8,
  },
  textInput: {
    width: '100%',
    height: 56,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 16.8,
    color: '#151515',
  },
  helperText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: '#757C85',
    lineHeight: 16,
  },
  resultContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  daysNumber: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    flex: 1,
  },
  daysLabel: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    textAlign: 'right',
    flex: 1,
  },
  buttonContainer: {
    paddingHorizontal: 24,
  },
});
